import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { toast } from "sonner";

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem("lw_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (adminKey) => {
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/subscribers`, { headers: { "X-Admin-Key": adminKey } }),
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Key": adminKey } }),
      ]);
      setSubs(subsRes.data || []);
      setStats(statsRes.data || null);
      setAuthed(true);
      localStorage.setItem("lw_admin_key", adminKey);
    } catch (e) {
      toast.error("Wrong key or server error.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (key) load(key);
  };

  const logout = () => {
    setAuthed(false);
    setKey("");
    localStorage.removeItem("lw_admin_key");
  };

  const downloadCsv = () => {
    const url = `${API}/admin/subscribers.csv?key=${encodeURIComponent(key)}`;
    window.open(url, "_blank");
  };

  if (!authed) {
    return (
      <section className="pt-32 md:pt-40 pb-24">
        <div className="max-w-md mx-auto px-6">
          <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">Admin</div>
          <h1 className="serif text-4xl leading-tight text-espresso">Charlie's ledger.</h1>
          <p className="mt-3 text-charcoal/80 text-sm">Enter your admin key to view the waitlist.</p>
          <form onSubmit={submit} className="mt-8">
            <label className="block">
              <span className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">Admin key</span>
              <input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                data-testid="admin-key-input"
                className="mt-2 w-full bg-transparent border-b border-black/20 focus:border-[#4A7C7B] outline-none py-3 text-lg mono"
                placeholder="•••••••••"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#2C241B] text-[#FAF8F5] px-6 py-3 hover:bg-[#4A7C7B] transition-colors disabled:opacity-60"
            >
              {loading ? "Checking…" : "Enter"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-32 md:pt-36 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-2">Waitlist admin</div>
            <h1 className="serif text-4xl md:text-5xl text-espresso">Charlie's ledger.</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={downloadCsv}
              data-testid="admin-download-csv"
              className="rounded-full border border-black/10 px-5 py-2.5 text-sm hover:bg-[#2C241B] hover:text-[#FAF8F5] transition-colors"
            >
              Download CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="rounded-full border border-black/10 px-5 py-2.5 text-sm hover:bg-[#D28A7C] hover:text-[#FAF8F5] transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Total signups" value={stats?.total ?? "—"} testid="stat-total" />
          <StatCard label="Parents" value={stats?.by_role?.find((r) => r.role === "parent")?.count ?? 0} testid="stat-parents" />
          <StatCard label="Therapists" value={stats?.by_role?.find((r) => r.role === "therapist")?.count ?? 0} testid="stat-therapists" />
          <StatCard label="Educators" value={stats?.by_role?.find((r) => r.role === "educator")?.count ?? 0} testid="stat-educators" />
        </div>

        <div className="rounded-2xl border border-black/10 overflow-hidden">
          <table data-testid="subscribers-table" className="w-full text-sm">
            <thead className="bg-[#F0EBE1]/70">
              <tr className="text-left">
                <th className="mono text-[10px] tracking-[0.24em] uppercase text-charcoal/80 px-5 py-4">Email</th>
                <th className="mono text-[10px] tracking-[0.24em] uppercase text-charcoal/80 px-5 py-4">Name</th>
                <th className="mono text-[10px] tracking-[0.24em] uppercase text-charcoal/80 px-5 py-4">Role</th>
                <th className="mono text-[10px] tracking-[0.24em] uppercase text-charcoal/80 px-5 py-4">Source</th>
                <th className="mono text-[10px] tracking-[0.24em] uppercase text-charcoal/80 px-5 py-4">Joined</th>
              </tr>
            </thead>
            <tbody>
              {subs.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-charcoal/60 italic-serif">No signups yet — the list is quiet.</td></tr>
              )}
              {subs.map((s) => (
                <tr key={s.id} className="border-t border-black/5 hover:bg-[#FAF8F5]/60">
                  <td className="px-5 py-3 mono text-espresso">{s.email}</td>
                  <td className="px-5 py-3">{s.first_name || <span className="text-charcoal/40">—</span>}</td>
                  <td className="px-5 py-3">{s.role || <span className="text-charcoal/40">—</span>}</td>
                  <td className="px-5 py-3">{s.source || <span className="text-charcoal/40">direct</span>}</td>
                  <td className="px-5 py-3 mono text-charcoal/80 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-[#FFFDF9] p-6">
      <div className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">{label}</div>
      <div data-testid={testid} className="serif text-5xl mt-3 text-espresso tabular-nums">{value}</div>
    </div>
  );
}
